import { useMemo } from 'react'
import {
  CAvatar,
  CBadge,
  CDropdown,
  CDropdownDivider,
  CDropdownHeader,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CTooltip,
} from '@coreui/react'
import {
  cilCreditCard,
  cilFile,
  cilLockLocked,
  cilUser,
  cilAccountLogout,
} from '@coreui/icons'
import CIcon from '@coreui/icons-react'

import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { logout } from '../../store/slices/authSlice'
import { formatRoleName } from '../../utils/formatters'

// Menu item configuration
const menuItemsConfig = {
  profile: {
    path: '/profile',
    icon: cilUser,
    label: 'Profile',
    badge: false,
    roles: ['super-admin', 'ceo', 'coordinator', 'professor', 'student', 'parent']
  },
  payments: {
    path: '/payments',
    icon: cilCreditCard,
    label: 'Payments',
    badge: true,
    badgeColor: 'secondary',
    badgeCount: 0,
    roles: ['super-admin', 'ceo', 'coordinator', 'student', 'parent']
  },
  projects: {
    path: '/projects',
    icon: cilFile,
    label: 'Projects',
    badge: true,
    badgeColor: 'primary',
    badgeCount: 0,
    roles: ['super-admin', 'ceo', 'coordinator']
  },
  'lock-account': {
    path: '/lock-account',
    icon: cilLockLocked,
    label: 'Lock Account',
    badge: false,
    roles: ['super-admin', 'ceo', 'coordinator']
  }
}

const AppHeaderDropdown = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user } = useSelector((state) => state.auth)

  const handleLogout = () => {
    dispatch(logout())
    navigate('/login')
  }

  const handleNavigation = (path) => {
    navigate(path)
  }

  // Get menu items based on user role
  const visibleMenuItems = useMemo(() => {
    if (!user?.role) return ['profile']
    
    return Object.keys(menuItemsConfig).filter(key => 
      menuItemsConfig[key].roles.includes(user.role)
    )
  }, [user?.role])

  // Get user initials
  const getUserInitials = () => {
    const firstName = user?.first_name?.charAt(0) || ''
    const lastName = user?.last_name?.charAt(0) || ''
    return `${firstName}${lastName}`.toUpperCase()
  }

  // Build tooltip content
  const tooltipContent = `${user?.first_name} ${user?.last_name} - ${formatRoleName(user?.role)}`

  return (
    <CDropdown variant="nav-item">
      <CDropdownToggle placement="bottom-end" className="py-0" caret={false}>
        <CTooltip content={tooltipContent} placement="bottom-end">
          <div style={{ display: 'inline-block' }}>
            {user?.profile_pic ? (
              <CAvatar
                src={user.profile_pic}
                size="md"
                status="success"
                style={{
                  objectFit: 'cover',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%'
                }}
              />
            ) : (
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#e9ecef',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  color: '#6c757d',
                  fontSize: '14px',
                  border: '2px solid #28a745'
                }}
              >
                {getUserInitials()}
              </div>
            )}
          </div>
        </CTooltip>
      </CDropdownToggle>
      <CDropdownMenu className="pt-0 pr-5 w-auto">
        <CDropdownHeader className="bg-light fw-semibold py-2">
          {user?.first_name} {user?.last_name}
        </CDropdownHeader>
        
        {visibleMenuItems.map((itemKey) => {
          const item = menuItemsConfig[itemKey]
          return (
            <CDropdownItem 
              key={itemKey}
              onClick={() => handleNavigation(item.path)}
              style={{ cursor: 'pointer' }}
            >
              <CIcon icon={item.icon} className="me-2" />
              {item.label}
              {item.badge && item.badgeCount > 0 && (
                <CBadge color={item.badgeColor} className="ms-2">
                  {item.badgeCount}
                </CBadge>
              )}
            </CDropdownItem>
          )
        })}
        
        <CDropdownDivider />
        <CDropdownItem onClick={handleLogout} style={{ cursor: 'pointer' }}>
          <CIcon icon={cilAccountLogout} className="me-2" />
          Logout
        </CDropdownItem>
      </CDropdownMenu>
    </CDropdown>
  )
}

export default AppHeaderDropdown